import React from "react";
import {AutoSizer, InfiniteLoader, ScrollSync} from "react-virtualized";
import {CancelablePromise} from "mn-utils/global";
import formatTime from 'mn-utils/formatTime';
import withState from "../../../../../utils/withState";
import {IntervalFilterMerchant} from '../../../../dumb/IntervalFilterCustom';

import 'react-virtualized/styles.css';

import TableView from './components/TableView';
import GridView from './components/GridView';
import FiltersDisplay from './components/FiltersDisplay';

import {INTERVAL_YEAR} from "../../../../dumb/IntervalFilterCustom/constants/intervals";
import {tableFilters$} from "./utils/tableFilters";
import {handleFiltersData} from "./utils/handleFiltersData";
import getDateRange from "../../../../dumb/IntervalFilterCustom/utils/getDateRange";
import {INTERVAL_MONTH} from "./components/FiltersDisplay/constants/intervals";
import noop from "mn-utils/noop";
import {Box} from "@material-ui/core";

// add to ctor additional abstraction layer with
// {sortProps, onSort, filter, onSelectFilterChange}

export const customTableCtor = (displayProps) => withState((setState, self, onMount) => {
  const {
    rowHeight,
    headerHeight,
    className
  } = displayProps;

  const stylingProps = {
    className,
    headerRowClass: `${className}__header-row`,
    headerColClass: `${className}__header-col`,
    rowClass: `${className}__row`,
    colClass: `${className}__col`,
    actionClass: `${className}__actions`,
    noDataClass: `${className}__no-data`
  }

  const columns = self.props.columns || [];

  let constantWidth = 0;
  let stretchedCols = 0;

  const columnsTotalWidth = columns.reduce((acc, next) => {
    if (next.width) {
      constantWidth += next.width;
    } else if (next.minWidth) {
      stretchedCols += 1;
    }

    acc += (next.minWidth || next.width || 0);
    return acc;
  }, 0);

  const defaultFiltersValue = !self.props.disableFilters
    ? (
      tableFilters$.getValue()
      || self.props.filters
      || {
        interval: INTERVAL_MONTH,
        ...getDateRange({interval: INTERVAL_MONTH})
      }
    ) : {};

  const LIMIT = 50;

  self.state = {
    filters: defaultFiltersValue,
    sort: {},
    list: [],
    rowCount: 0,
    reloadOnPropsChange: self.props.reloadOnPropsChange,
    loading: true
  }

  const rowsDataGetter = (props = {}) => {
    const offset = (
      props.offset || props.offset === 0
        ? props.offset
        : (self.state.list || []).length
    );

    const filters = !self.props.disableFilters
      ? handleFiltersData(props.filters || self.state.filters)
      : {};

    const getDataFromServer = self.props.getDataFromServer || CancelablePromise;

    return getDataFromServer({offset, limit: LIMIT, ...filters});
  }

  const loadMoreRows = (props = {}) => {

    return rowsDataGetter(props)
        .then(({count, list}) => {
          const nextList = [...self.state.list, ...list];

          setState({
            list: nextList,
            rowCount: nextList.length < count ? nextList.length + 1 : count,
            loading: false
          });
        })
        .catch(() => {
          setState({
            list: [],
            rowCount: 0,
            loading: false
          });
        })
  }

  const isRowLoaded = ({ index }) =>  {
    return !!self.state.list[index];
  }

  const onFiltersChange = (filters = {}) => {
    setState({
      filters,
      list: [],
      rowCount: 0,
      loading: true
    });

    setTimeout(() => {
      loadMoreRows({...filters});
    }, 100);
  }

  const onReset = () => {
    setState({
      list: [],
      rowCount: 0,
      loading: true
    });

    setTimeout(() => {
      loadMoreRows();
    }, 100);
  }

  const requestReducer = ({offset = 0, acc = [], totalCount, limit}) => {
    if (acc.length >= limit) {
      return new Promise((resolve) => resolve({list: acc, count: totalCount}));
    }

    return rowsDataGetter({offset})
        .then(({count, list}) => {
          const nextList = [...acc, ...list];
          const nextLimit = limit > count ? count : limit;

          return requestReducer({
            offset: nextList.length,
            acc: nextList,
            limit: nextLimit,
            totalCount: count
          })
        })
  }

  const update = () => {
    const limit = self.state.list.length || 50;

    setState({
      loading: true,
      list: [],
      rowCount: 0,
    });

    return requestReducer({limit})
        .then(({list, count}) => {
          setState({
            list: list,
            rowCount: list.length < count ? list.length + 1 : count,
            loading: false
          });
        })
  }

  onMount(() => {
    loadMoreRows().finally(() => setState({loading: false}));
  })

  return (state, props) => {

    if (
      !state.loading
      && props.reloadOnPropsChange
      && state.reloadOnPropsChange !== props.reloadOnPropsChange
    ) {
      setState({
        loading: true,
        reloadOnPropsChange: props.reloadOnPropsChange
      });

      onReset();
    }

    let tableHeightMinus = 0;

    if (props.header) tableHeightMinus += 45;
    if (!props.disableFilters) tableHeightMinus += 75;

    return (
      <div className="h">
        {!!props.header && props.header(update)}
        {!props.disableFilters && (
          <div className="mt10">
            <FiltersDisplay
              columnsTotalWidth={columnsTotalWidth}

              report={props.report}
              filtersParams={props.filtersParams}
              hideSearch={props.hideSearch}

              defaultFiltersData={state.filters}
              noData={!state.rowCount}

              onFiltersChange={onFiltersChange}
              update={update}
            />
          </div>
        )}
        <Box height={tableHeightMinus ? `calc(100% - ${tableHeightMinus}px)` : '100%'}>
          <InfiniteLoader
            isRowLoaded={isRowLoaded}
            loadMoreRows={loadMoreRows}
            rowCount={state.rowCount}
          >
            {({onRowsRendered, registerChild}) => (
              <AutoSizer>
                {({width, height}) => {
                  return width > columnsTotalWidth
                    ? (
                      <TableView
                        registerChild={registerChild}
                        onRowsRendered={onRowsRendered}

                        width={width}
                        height={height}

                        constantWidth={constantWidth}
                        stretchedCols={stretchedCols}

                        headerHeight={headerHeight}
                        rowHeight={rowHeight}

                        update={update}

                        {...stylingProps}

                        columns={props.columns}
                        filtersParams={props.filtersParams}
                        disableFilters={props.disableFilters}
                        actions={props.actions}

                        list={state.list}
                        rowCount={state.rowCount}
                        loading={state.loading}

                        filtersData={state.filters}
                        disableHeadFilters={width < 995}
                        onFiltersChange={onFiltersChange}
                      />
                    ) : (
                      <GridView
                        registerChild={registerChild}
                        onRowsRendered={onRowsRendered}

                        width={width}
                        height={height}

                        constantWidth={constantWidth}
                        stretchedCols={stretchedCols}
                        columnsTotalWidth={columnsTotalWidth}

                        update={update}

                        headerHeight={headerHeight}
                        rowHeight={rowHeight}

                        {...stylingProps}

                        columns={props.columns}
                        filtersParams={props.filtersParams}
                        disableFilters={props.disableFilters}
                        actions={props.actions}

                        list={state.list}
                        rowCount={state.rowCount}
                        loading={state.loading}
                      />
                    )
                }}
              </AutoSizer>
            )}
          </InfiniteLoader>
        </Box>
      </div>
    )
  }
});


